import React, { useState, useEffect } from 'react';
import _get from 'lodash/get';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useStatus, useData, isLoading, isNormal, isError, combineStatuses } from 'resift';
import usePrevious from '@sift/resift/usePrevious';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import showSnackbar from 'helpers/showSnackbar';
import createEntity from 'fetches/createEntity';
import convertStringToArrayOfStrings from 'helpers/convertStringToArrayOfStrings';
import * as Types from 'pages/Pages/typings';
import makeGetEntitiesListFetch from 'fetches/makeGetEntitiesListFetch';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import TextField from '@material-ui/core/TextField';
import CancelButton from 'components/Page/CancelButton';
import LoadingButton from 'atoms/LoadingButton';

interface Props {
  open: boolean;
  onSetOpen: (state: boolean) => void;
}

function CreatePageModal({ open, onSetOpen }: Props) {
  const classes = useStyles();
  let { entityTypeKey } = useParams<{ entityTypeKey: string }>();
  const dispatch = useDispatch();
  const createEntityStatus = useStatus(createEntity);
  const createEntityData = useData(createEntity);
  const prevCreateEntityStatus = usePrevious(createEntityStatus);
  const [pageNameInput, setPageNameInput] = useState('');
  const actions = _get(createEntityData, 'actions', []);
  const numberOfEntitiesCreated = actions.length;

  // entities list
  const makeGetEntitiesList = makeGetEntitiesListFetch();
  const entitiesListStatus = useStatus(makeGetEntitiesList);

  const combinedStatus = combineStatuses(createEntityStatus, entitiesListStatus);

  const handleCreatePage = async () => {
    const nameInputArray = convertStringToArrayOfStrings(pageNameInput);
    const actions: Types.BatchAction[] | null =
      nameInputArray &&
      nameInputArray.map(name => ({
        type: 'create',
        data: { name, entityTypeId: entityTypeKey },
      }));
    actions && (await dispatch(createEntity({ entityTypeId: entityTypeKey, actions })));
    dispatch(makeGetEntitiesList({ query: { entityTypeId: entityTypeKey } }));
  };

  const handlePageNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNameInput(e.target.value);
  };

  const handleCancel = () => {
    if (!isLoading(combinedStatus)) {
      onSetOpen(false);
      setPageNameInput('');
    }
  };

  useEffect(() => {
    if (isLoading(prevCreateEntityStatus) && isNormal(combinedStatus)) {
      onSetOpen(false);
      setPageNameInput('');
    } else if (isLoading(prevCreateEntityStatus) && isError(createEntityStatus)) {
      setPageNameInput('');
    }
  }, [prevCreateEntityStatus, combinedStatus, createEntityStatus]);

  showSnackbar({
    status: createEntityStatus,
    message: `${numberOfEntitiesCreated} new pages created.`,
    errorMessage: _get(createEntityData, 'errorMessage', 'There was an error creating your pages'),
    variant: 'success',
  });

  return (
    <Dialog data-testid="create-page-modal" open={open} onClose={handleCancel}>
      <DialogTitle>Create New Pages</DialogTitle>
      <DialogContent className={classes.marginBottom}>
        <DialogContentText>
          Enter one or more names of pages to be created, separated by comma. If you include a name
          where an associated page already exists, it will be ignored.
        </DialogContentText>
        <TextField
          id="page name input"
          fullWidth={true}
          value={pageNameInput}
          multiline={true}
          onChange={handlePageNameChange}
          inputProps={{ 'aria-label': 'page-name-input' }}
        />
      </DialogContent>
      <DialogActions>
        <CancelButton disabled={isLoading(combinedStatus)} onClick={handleCancel} />
        <LoadingButton
          variant="contained"
          color="secondary"
          disabled={pageNameInput === ''}
          onClick={handleCreatePage}
          loading={isLoading(combinedStatus)}
          label="Create"
          spinnerClassName={classes.loadingButtonSpinner}
        />
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles((theme: any) => ({
  marginBottom: {
    marginBottom: theme.spacing(3),
  },
  loadingButtonSpinner: {
    color: theme.palette.common.white,
  },
}));

export default CreatePageModal;
